(() => {
  const COMPATIBILITY_URL = '/apps/storefront/compatibility';

  const slugify = (value) => value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  class ReverseLookup {
    constructor(element) {
      this.el = element;
      this.brandsData = null;
      this.brandNames = [];
      this.activeBrand = null;

      this.body = this.el.querySelector('.reverse-lookup__body');
      this.compatibilityUrl = this.el.dataset.compatibilityUrl || COMPATIBILITY_URL;
      this.sku = this.el.dataset.sku || '';
      this.loadingLabel = this.el.dataset.loadingLabel || 'Loading';
      this.infoText = this.el.dataset.info || '';
      this.brandLabel = this.el.dataset.brandLabel || '';
      this.searchPlaceholder = this.el.dataset.searchPlaceholder || '';
      this.modelsCountLabel = this.el.dataset.modelsCount || '';
      this.selectedBrandLabel = this.el.dataset.selectedBrandLabel || '';
      this.chooseAnotherLabel = this.el.dataset.chooseAnother || '';
      this.compatibleModelsLabel = this.el.dataset.compatibleModels || '';
      this.emptyLabel = this.el.dataset.emptyLabel || '';
      this.errorLabel = this.el.dataset.errorLabel || '';
      this.searchIconHtml = this.el.querySelector('.reverse-lookup__icon-search')?.innerHTML || '';

      this.section = this.el.closest('.shopify-section');
      if (this.section) {
        this.section.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      this.loadBrands();
    }

    handleVariantChange(event) {
      const sku = event.detail?.variant?.sku || '';
      if (sku === this.sku) return;

      this.sku = sku;
      this.activeBrand = null;
      this.el.hidden = true;
      this.body.innerHTML = `<p class="reverse-lookup__loading">${this.escapeHtml(this.loadingLabel)}</p>`;
      this.loadBrands();
    }

    withCount(template, count) {
      return String(template).replace(/__COUNT__/g, String(count));
    }

    async loadBrands() {
      this.el.hidden = true;

      if (!this.sku) {
        this.renderError();
        return;
      }

      try {
        const url = `${this.compatibilityUrl}?sku=${encodeURIComponent(this.sku)}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to load compatibility (${response.status})`);
        }

        const data = await response.json();
        const turntables = data?.compatible_turntables;

        if (!Array.isArray(turntables)) {
          throw new Error('Invalid compatibility data');
        }

        const brands = {};

        turntables.forEach((entry) => {
          const brand = entry?.brand;
          const models = entry?.models;

          if (brand && Array.isArray(models) && models.length) {
            brands[brand] = models;
          }
        });

        this.brandsData = brands;
        this.brandNames = Object.keys(brands).sort((a, b) => a.trim().localeCompare(b.trim()));
        this.renderBrands();
      } catch (error) {
        console.error('Reverse lookup:', error);
        this.renderError();
      }
    }

    renderBrands() {
      if (!this.brandNames.length) {
        this.body.innerHTML = '';
        return;
      }

      const searchId = `reverse-lookup-search-${slugify(this.sku) || 'sku'}`;
      const brandButtons = this.brandNames.map((brand, index) => {
        const brandId = slugify(brand);
        const modelCount = this.brandsData[brand].length;

        return `
          <button
            type="button"
            class="btn btn--secondary btn--sm reverse-lookup__brand"
            data-brand-index="${index}"
            data-brand-id="${brandId}"
          >
            <span class="reverse-lookup__brand-name">${this.escapeHtml(brand.trim())}</span>
            <span class="reverse-lookup__brand-count">${this.escapeHtml(this.withCount(this.modelsCountLabel, modelCount))}</span>
          </button>
        `;
      }).join('');

      this.body.innerHTML = `
        <p class="reverse-lookup__info">${this.escapeHtml(this.infoText)}</p>
        <div class="reverse-lookup__browse">
          <label class="reverse-lookup__brand-label" for="${searchId}">${this.escapeHtml(this.brandLabel)}</label>
          <div class="reverse-lookup__search relative">
            <input
              id="${searchId}"
              class="input search__input w-full reverse-lookup__search-input"
              type="search"
              autocomplete="off"
              placeholder="${this.escapeHtml(this.withCount(this.searchPlaceholder, this.brandNames.length))}"
            >
            <span class="search__submit reverse-lookup__search-icon text-current absolute start" aria-hidden="true">${this.searchIconHtml}</span>
          </div>
          <div class="reverse-lookup__brand-list">
            ${brandButtons}
          </div>
        </div>
        <div class="reverse-lookup__selected" hidden>
          <div class="reverse-lookup__selected-card">
            <div class="reverse-lookup__selected-header">
              <div class="reverse-lookup__selected-meta">
                <p class="reverse-lookup__selected-kicker">${this.escapeHtml(this.selectedBrandLabel)}</p>
                <p class="reverse-lookup__selected-name"></p>
              </div>
              <button type="button" class="btn btn--secondary btn--sm reverse-lookup__choose-another">
                ${this.escapeHtml(this.chooseAnotherLabel)}
              </button>
            </div>
            <p class="reverse-lookup__compatible-count"></p>
            <ul class="reverse-lookup__list"></ul>
          </div>
        </div>
      `;

      this.browse = this.body.querySelector('.reverse-lookup__browse');
      this.selected = this.body.querySelector('.reverse-lookup__selected');
      this.brandList = this.body.querySelector('.reverse-lookup__brand-list');
      this.searchInput = this.body.querySelector('.reverse-lookup__search-input');
      this.selectedName = this.body.querySelector('.reverse-lookup__selected-name');
      this.compatibleCount = this.body.querySelector('.reverse-lookup__compatible-count');
      this.modelsList = this.body.querySelector('.reverse-lookup__list');
      this.chooseAnother = this.body.querySelector('.reverse-lookup__choose-another');

      this.brandList.addEventListener('click', this.handleBrandClick.bind(this));
      this.searchInput.addEventListener('input', this.handleSearchInput.bind(this));
      this.chooseAnother.addEventListener('click', this.handleChooseAnother.bind(this));
      this.el.hidden = false;
    }

    handleSearchInput() {
      const query = this.searchInput.value.trim().toLowerCase();
      let visibleCount = 0;

      this.brandList.querySelectorAll('.reverse-lookup__brand').forEach((button) => {
        const name = button.querySelector('.reverse-lookup__brand-name')?.textContent || '';
        const match = !query || name.toLowerCase().includes(query);
        button.hidden = !match;
        if (match) visibleCount += 1;
      });

      this.brandList.hidden = visibleCount === 0;
    }

    handleBrandClick(event) {
      const button = event.target.closest('.reverse-lookup__brand');
      if (!button) return;

      const brandIndex = Number(button.dataset.brandIndex);
      const brand = this.brandNames[brandIndex];
      if (!brand) return;

      this.setActiveBrand(brand);
    }

    setActiveBrand(brand) {
      this.activeBrand = brand;
      const models = Array.isArray(this.brandsData[brand]) ? this.brandsData[brand] : [];

      this.selectedName.textContent = brand.trim();
      this.compatibleCount.textContent = this.withCount(this.compatibleModelsLabel, models.length);
      this.modelsList.innerHTML = models.map((model, modelIndex) => (
        `<li class="reverse-lookup__item" data-index="${modelIndex}">${this.escapeHtml(model)}</li>`
      )).join('');

      this.browse.hidden = true;
      this.selected.hidden = false;
    }

    handleChooseAnother() {
      this.activeBrand = null;
      this.modelsList.innerHTML = '';
      this.selected.hidden = true;
      this.browse.hidden = false;

      if (this.searchInput) {
        this.searchInput.value = '';
        this.handleSearchInput();
      }
    }

    renderError() {
      this.el.hidden = true;
      this.body.innerHTML = '';
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  const initReverseLookup = () => {
    document.querySelectorAll('.reverse-lookup:not([data-initialized])').forEach((element) => {
      element.dataset.initialized = 'true';
      new ReverseLookup(element);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReverseLookup);
  } else {
    initReverseLookup();
  }
})();
